import { useTheme } from '@mui/material';
import { formatCurrency, normalizeAddDigits } from 'app/utils/normalizer';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import { Heading4, TextM } from 'app/components';
import {
  ReviewSimulationBox,
  ReviewSimulationPreviewItem,
  ReviewSimulationPreviewItems,
  ReviewStepBox,
} from '../../styles';
import { ReviewStepSimulationDivider } from '../ReviewStepSimulationDivider';
import { SimulationOld } from '../../utils/borrowerTypeParser';

interface ReviewStepSimulationOldProps {
  simulation: SimulationOld | null;
}

const ReviewStepSimulationOld = ({
  simulation,
}: ReviewStepSimulationOldProps) => {
  const theme = useTheme();

  if (!simulation) {
    return null;
  }

  return (
    <ReviewStepBox marginTop="0px" marginBottom="16px">
      <Heading4
        style={{
          color: theme.palette.brand.secondary.base,
        }}
      >
        Valores da solicitação:
      </Heading4>
      <ReviewSimulationBox>
        <ReviewSimulationPreviewItems>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.primary.base }}
              >
                Quant. parcelas
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.numPeriods && simulation?.numPeriods > 0
                  ? simulation?.numPeriods
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor da parcela
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.paymentAmount
                  ? formatCurrency(simulation?.paymentAmount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor solicitado
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.requested_amount
                  ? formatCurrency(simulation?.requested_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor financiado
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.financed_amount
                  ? formatCurrency(simulation?.financed_amount || 0)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Juros
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.interestRate
                  ? `${normalizeAddDigits(
                      simulation?.interestRate * 100,
                      2,
                    )?.replace('.', ',')}% a.m.`
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                CET
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {typeof simulation?.monthlyEffectiveInterestRate === 'number'
                  ? `${normalizeAddDigits(
                      simulation?.monthlyEffectiveInterestRate * 100,
                      2,
                    ).replace('.', ',')}% a.m.`
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
        </ReviewSimulationPreviewItems>
        <ReviewSimulationPreviewItems>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                TC
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.tacAmount
                  ? formatCurrency(simulation?.tacAmount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                IOF
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.iofAmount
                  ? formatCurrency(simulation?.iofAmount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor do Seguro
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.insurance_amount
                  ? formatCurrency(simulation?.insurance_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Primeiro vencimento
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.firstPaymentDate
                  ? formatDateFromBackend(
                      simulation?.firstPaymentDate,
                    ).toLocaleDateString('pt-br')
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Último vencimento
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.lastPaymentDate
                  ? formatDateFromBackend(
                      simulation?.lastPaymentDate,
                    ).toLocaleDateString('pt-br')
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Saldo Total
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulation?.totalAmountOwed
                  ? formatCurrency(simulation?.totalAmountOwed)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
        </ReviewSimulationPreviewItems>
      </ReviewSimulationBox>
    </ReviewStepBox>
  );
};

export default ReviewStepSimulationOld;
